import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { toast } from 'sonner';

export default function Optimizer() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [deepAnalyzing, setDeepAnalyzing] = useState(false);
  const [deploying, setDeploying] = useState({});
  const [manualInput, setManualInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/optimizer/suggestions`);
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch (err) {
      toast.error('Fehler beim Laden');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/optimizer/analyze`, { method: 'POST' });
      const data = await res.json();
      toast.success(`${data.new_optimizations} neue Vorschläge gefunden!`);
      fetchSuggestions();
    } catch (err) {
      toast.error('Analyse fehlgeschlagen');
    }
    setAnalyzing(false);
  };

  const runDeepAnalysis = async () => {
    setDeepAnalyzing(true);
    toast.info('Deep Analysis läuft... Das kann 1-2 Minuten dauern.');
    try {
      const res = await fetch(`${API}/optimizer/deep-analyze`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.issues_found} Issues gefunden, ${data.issues_saved} neu!`);
        fetchSuggestions();
      } else {
        toast.error(data.message || 'Analyse fehlgeschlagen');
      }
    } catch (err) {
      toast.error('Deep Analyse fehlgeschlagen');
    }
    setDeepAnalyzing(false);
  };

  const submitManualIssue = async () => {
    if (!manualInput.trim()) {
      toast.error('Bitte Problem beschreiben');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/optimizer/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: manualInput.trim() })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Optimierung erstellt!');
        setManualInput('');
        fetchSuggestions();
      } else {
        toast.error(data.message || 'Fehler beim Erstellen');
      }
    } catch (err) {
      toast.error('Fehler beim Senden');
    }
    setSubmitting(false);
  };

  const cleanupExternalUrls = async () => {
    setCleaning(true);
    try {
      const res = await fetch(`${API}/optimizer/cleanup-external-urls`, { method: 'POST' });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`${data.deleted} externe URLs gelöscht`);
        fetchSuggestions();
      } else {
        toast.error('Cleanup fehlgeschlagen');
      }
    } catch (err) {
      toast.error('Cleanup fehlgeschlagen');
    }
    setCleaning(false);
  };

  const deployNow = async (id) => {
    setDeploying(prev => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API}/optimizer/deploy/${id}?environment=live`, { 
        method: 'POST' 
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Erfolgreich deployed! Cache leeren nicht vergessen.');
        fetchSuggestions();
      } else {
        toast.error(data.message || 'Deployment fehlgeschlagen');
        if (data.manual_required) {
          toast.info(data.instruction);
        }
      }
    } catch (err) {
      toast.error('Fehler beim Deployment');
    }
    setDeploying(prev => ({ ...prev, [id]: false }));
  };

  const rejectSuggestion = async (id) => {
    try {
      await fetch(`${API}/optimizer/reject/${id}`, { method: 'POST' });
      toast.success('Abgelehnt');
      fetchSuggestions();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const getImpactColor = (impact) => {
    if (impact === 'high') return 'bg-red-100 text-red-800 border-red-200';
    if (impact === 'medium') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'meta': '🔍',
      'headline': '📝',
      'cta': '🔘',
      'trust_element': '✅',
      'text_copy': '📄'
    };
    return icons[type] || '📦';
  };

  return (
    <div className="p-4 max-w-4xl mx-auto" data-testid="optimizer-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Website-Analyzer
        </h1>
        <p className="text-gray-600 text-sm">
          Automatische Analyse und Optimierung von adagmedia.ch
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={runAnalysis}
          disabled={analyzing || deepAnalyzing}
          className="flex-1 bg-blue-600 text-white py-3 px-3 rounded-lg font-medium text-sm
                     disabled:bg-blue-400 active:bg-blue-700 transition-colors"
          data-testid="analyze-btn"
        >
          {analyzing ? '...' : '🔍 Quick'}
        </button>
        <button
          onClick={runDeepAnalysis}
          disabled={analyzing || deepAnalyzing}
          className="flex-1 bg-purple-600 text-white py-3 px-3 rounded-lg font-medium text-sm
                     disabled:bg-purple-400 active:bg-purple-700 transition-colors"
          data-testid="deep-analyze-btn"
        >
          {deepAnalyzing ? '⏳ Läuft...' : '🔬 Deep Scan'}
        </button>
        <a
          href="/seo"
          className="flex-1 bg-slate-700 text-white py-3 px-3 rounded-lg font-medium text-sm
                     text-center hover:bg-slate-800 transition-colors"
        >
          📊 SEO Details
        </a>
      </div>

      {/* Manual Issue Input */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          ✏️ Problem manuell eingeben
        </label>
        <textarea
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder="z.B. 'Der CTA auf der Produktseite ist zu klein' oder 'Meta Description für Bachelorarbeit-Seite fehlt USP'"
          className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          data-testid="manual-input"
        />
        <button
          onClick={submitManualIssue}
          disabled={submitting || !manualInput.trim()}
          className="mt-3 w-full bg-gray-900 text-white py-2.5 px-4 rounded-lg font-medium 
                     disabled:bg-gray-400 active:bg-gray-800 transition-colors"
          data-testid="submit-manual-btn"
        >
          {submitting ? '⏳ Erstelle...' : '➕ Optimierung erstellen'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">
            {suggestions.length}
          </div>
          <div className="text-sm text-gray-500">Offene Vorschläge</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">
            {suggestions.filter(s => s.status === 'deployed').length}
          </div>
          <div className="text-sm text-gray-500">Deployed</div>
        </div>
        <button
          onClick={cleanupExternalUrls}
          disabled={cleaning}
          className="bg-red-50 rounded-lg p-4 border border-red-200 hover:bg-red-100 transition-colors text-left"
          data-testid="cleanup-btn"
        >
          <div className="text-sm font-medium text-red-700">
            {cleaning ? '⏳...' : '🗑️ Cleanup'}
          </div>
          <div className="text-xs text-red-500">Externe URLs löschen</div>
        </button>
      </div>

      {/* Suggestions List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Laden...</div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
          <div className="text-4xl mb-2">✨</div>
          <div className="text-gray-600">Keine offenen Vorschläge</div>
          <div className="text-sm text-gray-400 mt-1">
            Starte eine neue Analyse
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((s) => (
            <div 
              key={s.id} 
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              data-testid={`suggestion-${s.id}`}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getTypeIcon(s.type)}</span>
                    <div>
                      <div className="font-medium text-gray-900 capitalize">
                        {s.type.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-gray-500">{s.location}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getImpactColor(s.impact)}`}>
                      {s.impact === 'high' ? 'HIGH' : 'MEDIUM'}
                    </span>
                    {s.can_auto_deploy && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        AUTO
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Aktuell:</div>
                  <div className="text-sm text-gray-700 bg-red-50 p-2 rounded border border-red-100 break-words">
                    {s.current_value?.substring(0, 150)}
                    {s.current_value?.length > 150 && '...'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Vorschlag:</div>
                  <div className="text-sm text-gray-700 bg-green-50 p-2 rounded border border-green-100 break-words">
                    {s.suggested_value?.substring(0, 150)}
                    {s.suggested_value?.length > 150 && '...'}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  💡 {s.reasoning}
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
                {s.can_auto_deploy ? (
                  <button
                    onClick={() => deployNow(s.id)}
                    disabled={deploying[s.id]}
                    className="flex-1 bg-green-600 text-white py-2.5 px-4 rounded-lg font-medium 
                               disabled:bg-green-400 active:bg-green-700 transition-colors"
                    data-testid={`deploy-btn-${s.id}`}
                  >
                    {deploying[s.id] ? '⏳ Deploying...' : '🚀 Jetzt umsetzen'}
                  </button>
                ) : (
                  <div className="flex-1 bg-gray-200 text-gray-600 py-2.5 px-4 rounded-lg font-medium text-center">
                    🔧 Manuell ({s.deploy_method})
                  </div>
                )}
                <button
                  onClick={() => rejectSuggestion(s.id)}
                  className="bg-white text-gray-600 py-2.5 px-4 rounded-lg font-medium 
                             border border-gray-300 active:bg-gray-100 transition-colors"
                  data-testid={`reject-btn-${s.id}`}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cache Reminder */}
      <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-start gap-3">
          <span className="text-xl">⚠️</span>
          <div>
            <div className="font-medium text-amber-800">Nach dem Deployment</div>
            <div className="text-sm text-amber-700 mt-1">
              Magento Cache leeren: System → Cache Management → Flush
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
